import {
  Box,
  Button,
  Flex,
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useColorMode,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  defaultDurationFormats,
  defaultFractionFormats,
  numberFormatTabs,
} from './formatData';
import { IconType } from 'react-icons';
import {
  NegativeNumFormatType,
  NumberFormatConfigs,
  NumberFormatType,
} from '../../../../sheet/types';
import NumberFormatConfigurations from "./NumberFormatConfigurations";
import { useFormik } from 'formik';
import { createFormatCode } from '../../../../sheet/utils/numberFormat/formatCode';
import { showErrorToast } from '../../../../core/utils/view/toast';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../../core/hooks/storeHooks';
import { getContrastBackgroundColor1 } from '../../../../core/utils/view';
import { handleSheetOperations } from '../../../../sheet/redux/sagas/root/rootActions';
import { SheetCommandType } from '../../../../sheet/types/commands/sheetCommands/sheetCommands';
import { convertPositionToArea } from '../../../../sheet/utils/area/area';
import {
  selectSelectedSheetAreas,
  selectSheetView,
} from '../../../../sheet/selectors/sheetSelector';
import FooterControls from '../common/FooterControls';
import { getFormattedCell } from '../../../../sheet/utils/numberFormat/parser';

interface NumberFormatModalProps {
  sheetId: string;
  onClose: () => void;
}
const NumberFormatModal: React.FC<NumberFormatModalProps> = ({
  sheetId,
  onClose,
}) => {
  const stroke = useColorModeValue('#dfdfdf', '#2a2a2a'); //f1f5f8  /#
  const dispatch = useAppDispatch();

  const selectedAreas = useAppSelector((state) =>
    selectSelectedSheetAreas(state, sheetId)
  );
  // const selectedArea = useAppSelector((state) => getSheetview)

  const formik = useFormik<NumberFormatConfigs>({
    initialValues: {
      type: NumberFormatType.Automatic,
      decimalPlaces: '',
      showThousandSeparator: false,
      currencyCode: 'PKR',
      dateFormat: '',
      durationFormat: defaultDurationFormats[0],
      timeFormat: '',
      customCode: '',
      fractionFormat: defaultFractionFormats[0].code,
      negativeFormat: NegativeNumFormatType.StandardWithSign,
    },
    onSubmit: () => {},
  });

  const onApply = (formatCode: string) => {
    if (formik.values.type === NumberFormatType.DateNTime) {
      if (!formik.values.dateFormat && !formik.values.timeFormat)
        return showErrorToast('Please select a valida date or time format');
    }

    const format = {
      type: formik.values.type,
      code: formatCode,
    };

    dispatch(
      handleSheetOperations({
        sheetId,
        type: SheetCommandType.NumberFormat,
        formats: selectedAreas.map((area) => ({
          area,
          format: { ...format },
        })),
      })
    );

    // dispatch(
    //   handleCellOperationsAc({
    //     sheetId,
    //     operationData: {
    //       op: CellOperations.NumberFormat,
    //       type: formik.values.type,
    //       code: formatCode,
    //     },
    //   })
    // );
    onClose();
  };

  const getFormatTypeName = (type: NumberFormatType) => {
    if (type === NumberFormatType.Automatic) {
      return 'Automatic';
    } else if (type === NumberFormatType.Custom) {
      return 'Custom';
    }
  };

  const { colorMode } = useColorMode();
  const isDarkTheme = colorMode === 'dark';

  // const bg = tinycolor(stroke).lighten(9).toString()
  // const bg = tinycolor(stroke)
  //   .lighten(isDarkTheme ? 1 : 9)
  //   .toString();
  const bg = getContrastBackgroundColor1(stroke, isDarkTheme);

  const formatCode = createFormatCode(formik.values);

  // #region views
  const sideBarButtonView = (
    title: string,
    Icon: IconType,
    type: NumberFormatType | string
  ) => {
    const isActive = formik.values.type === type;
    return (
      <Button
        leftIcon={<Icon size="15px" />}
        size="sm"
        width="100%"
        rounded="xl"
        variant="ghost"
        fontWeight={isActive ? 'bold' : 'medium'}
        onClick={() => formik.setFieldValue('type', type)}
        isActive={isActive}
        key={title}
        fontSize="15px"
        m="1px 0px"
        justifyContent="start"
      >
        {title}
      </Button>
    );
  };

  const sideBarView = () => (
    <Flex
      width="32%"
      padding="10px"
      borderRight={`1px solid ${stroke}`}
      alignItems="center"
      flexDirection="column"
      background={bg}
    >
      {numberFormatTabs.map(({ title, Icon, formatType }) =>
        sideBarButtonView(title, Icon, formatType)
      )}
    </Flex>
  );

  const contentView = () => (
    <Flex
      width="68%"
      padding="10px"
      alignItems="center"
      justifyContent="center"
      textAlign="center"
    >
      <NumberFormatConfigurations formik={formik} />
    </Flex>
  );

  const formatCodeAndPreview = () => (
    <Flex alignItems="center" justifyContent="center" width="100%" m="7px 0px">
      {['Preview', 'Format Code'].map((title, index) => {
        let value = formatCode;
        let color = '';
        const isDate =
          formik.values.type === NumberFormatType.DateNTime ||
          formik.values.type == NumberFormatType.Duration;
        // let align = 'center';
        if (title.includes('Preview')) {
          const result = getFormattedCell(
            formik.values.type,
            formatCode,
            isDate ? '1/1' : '123',
            13,
            'Arial',
            105
          );
          if (result.str) value = result.str.toString();
          if (result.color) color = result.color;
          // if (result.align) align = result.align;
        }

        return (
          <Box width="50%" padding="2px" key={title}>
            <Text textAlign="center">{title}</Text>
            <Box
              border={`1px solid ${stroke}`}
              textAlign="center"
              padding="7px 5px"
              borderLeftRadius={index === 0 ? 'xl' : undefined}
              borderRightRadius={index === 0 ? undefined : 'xl'}
              fontSize="13px"
              background={bg}
              color={color}
            >
              {value.length ? value : getFormatTypeName(formik.values.type)}
            </Box>
          </Box>
        );
      })}
    </Flex>
  );

  const numberFormatView = () => (
    <Box padding="0px 10px">
      <Flex border={`1px solid ${stroke}`} rounded={'2xl'} overflow="hidden">
        {sideBarView()}
        {contentView()}
      </Flex>
      {formatCodeAndPreview()}
    </Box>
  );

  // const footerView = (
  //   <ModalFooter>
  //     <Button rounded="xl" size="sm" padding="2px 15px">
  //       Cancel
  //     </Button>
  //     <Button
  //       colorScheme="green"
  //       ml="2"
  //       rounded="xl"
  //       size="sm"
  //       padding="2px 15px"
  //       onClick={() => onApply(formatCode)}
  //     >
  //       Apply
  //     </Button>
  //   </ModalFooter>
  // );
  // #endregion
  return (
    <Modal onClose={onClose} isOpen={true} isCentered size="xl">
      <ModalOverlay />
      <ModalContent rounded="2xl" padding="0px">
        <ModalHeader>Number Formats</ModalHeader>
        <ModalCloseButton />
        {numberFormatView()}

        <FooterControls applyValue={formatCode} onApply={onApply} />
        {/* {footerView} */}
      </ModalContent>
    </Modal>
  );
};

export default NumberFormatModal;
